import axios from "axios"
import accessToken from "./jwt-token-access/accessToken"
import axiosRetry from "axios-retry"
import { createHmacSha256, generateStrongKey } from "common/utils"

// const API_URL = "http://16.24.15.232/api/v1/" // Sign burger Dev

const API_URL = "https://api.signsa.com/api/v1/"

const HMAC_SECRET_KEY = "wlPhIbITupF40SB2vjx5sltMRtbQ2zUwroLn4jSIKvM"

const axiosApi = axios.create({
  baseURL: API_URL,
})

axiosApi.interceptors.request.use(
  async config => {
    const token = await accessToken()
    if (token) {
      config.headers.Authorization = token
    }

    const language =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("I18N_LANGUAGE")
        : null
    config.headers["Accept-Language"] = language === "sa" ? "ar" : "en"

    // Add HMAC signature for PUT, PATCH, POST requests
    const method = config.method ? config.method.toUpperCase() : null
    if (method === "PUT" || method === "PATCH" || method === "POST") {
      try {
        // Get request body - it could be in data or in the config
        const requestBody = config.data || config.body || {}

        // Convert body to string for hashing
        let bodyString = ""
        if (requestBody instanceof FormData) {
          // For FormData, we need to handle it differently
          // Convert FormData to a string representation
          const formDataEntries = []
          for (const entry of requestBody.entries()) {
            formDataEntries.push(entry[0] + "=" + entry[1])
          }
          bodyString = formDataEntries.join("&")
        } else if (typeof requestBody === "string") {
          bodyString = requestBody
        } else if (requestBody && typeof requestBody === "object") {
          bodyString = JSON.stringify(requestBody)
        }

        // Create HMAC hash only if we have a body string
        if (bodyString) {
          const hash = await createHmacSha256(HMAC_SECRET_KEY, bodyString)
          if (hash) {
            config.headers["X-Signature"] = hash
          }
        }
      } catch (error) {
        console.error("Error generating HMAC signature:", error)
      }
    } else if (method === "GET" || method === "DELETE") {
      try {
        // Generate a strong key for GET/DELETE requests
        const generatedKey = generateStrongKey()

        // Add the generated key to x-st header
        config.headers["x-st"] = generatedKey

        // Create HMAC hash of the generated key
        const hash = await createHmacSha256(HMAC_SECRET_KEY, generatedKey)
        if (hash) {
          config.headers["X-Signature"] = hash
        }
      } catch (error) {
        console.error("Error generating HMAC signature for GET/DELETE:", error)
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

axiosApi.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status
    const statusCode = error?.response?.data?.statusCode
    const errorCode = error?.response?.data?.errorCode
    const msg = error?.response?.data?.msg

    if (status === 401 || statusCode === 401) {
      localStorage.clear()
      if (errorCode === "SESSION_REPLACED" && msg) {
        // Let App.js show the toast and do a client-side redirect once it
        // finishes (see its SESSION_REPLACED_TOAST_MS) instead of hard-
        // navigating here — a plain window event keeps this file decoupled
        // from the redux store / router (importing the store here would
        // create a circular dependency, since the store's action creators
        // import this same api_helper).
        window.dispatchEvent(
          new CustomEvent("auth:session-replaced", { detail: { msg } })
        )
      } else {
        window.location.href = "/login"
      }
    }

    return Promise.reject(error)
  }
)

axiosApi.interceptors.request.use(
  async config => {
    const token = await accessToken()
    if (token) {
      config.headers.Authorization = token
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Applying axios-retry to your axios instance
axiosRetry(axiosApi, {
  retries: 3, // Number of retries
  retryDelay: retryCount => {
    return retryCount * 1000 // Time between retries in ms
  },
  retryCondition: error => {
    // Retry on specific conditions
    return error.response.status === 500 || error.code === "ECONNABORTED"
  },
})

export function get(url, config) {
  config = config || {}
  return axiosApi.get(url, Object.assign({}, config)).then(function (response) {
    return response.data
  })
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then(response => response.data)
    .catch(err => {
      // Any error response with a body (400, 401, 403, 409, 429, 500, ...)
      // must reach the caller the same way a non-error response does, so
      // callers can read msg/errorCode/data (e.g. rate-limit remainingSeconds)
      // instead of silently getting undefined. Previously this only covered
      // status 400 with a `message` field — but this API never sends
      // `message`, only `msg`, so this branch effectively never matched and
      // every error response (429 rate limits included) was swallowed.
      if (err?.response?.data) {
        return err.response.data
      }
    })
}

export function put(url, data, config) {
  config = config || {}
  data = data || {}
  return axiosApi
    .put(url, Object.assign({}, data), Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
}

export function del(url, data, config) {
  config = config || {}
  data = data || {}
  return axiosApi
    .delete(url, { data: Object.assign({}, data) }, Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
}

export function patch(url, config) {
  config = config || {}
  return axiosApi
    .patch(url, Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
}

export function formDataPost(url, data, config) {
  config = config || {}
  return axiosApi
    .post(url, data, Object.assign({}, config)) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}

export function formDataPut(url, data, config) {
  config = config || {}
  return axiosApi
    .put(url, data, Object.assign({}, config)) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}

export function pdfDownload(url, data, config) {
  var requestConfig = Object.assign({}, config || {})
  requestConfig.responseType = "blob"
  return axiosApi
    .post(url, data, requestConfig) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}
